import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import type { FaceRecognitionDecisions } from '../../api/face-recognition.schemas';

interface DecisionDistributionChartProps {
  data?: FaceRecognitionDecisions;
  loading?: boolean;
  error?: Error;
  onRetry?: () => void;
}

/**
 * DecisionDistributionChart Component
 * 
 * Displays a chart showing Face Recognition decision distribution.
 * Shows Approved/Rejected/Error breakdowns in a visual format.
 * 
 * Requirements: 2.4, 6.2
 */
export function DecisionDistributionChart({ 
  data, 
  loading = false, 
  error,
  onRetry
}: DecisionDistributionChartProps) {
  
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Decision Distribution</CardTitle>
          </div>
          <CardDescription>Approved, Rejected, and Error breakdown</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 p-6 pt-0 space-y-4">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-3 h-3 rounded-full" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="w-full h-3 rounded-full" />
              </div>
            ))}
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-4 w-16 mb-2" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="text-center space-y-1">
                  <Skeleton className="h-4 w-8 mx-auto" />
                  <Skeleton className="h-3 w-12 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Decision Distribution</CardTitle>
          </div>
          <CardDescription>Failed to load decision data</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
            {onRetry && (
              <Button onClick={onRetry} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Decision Distribution</CardTitle>
          </div>
          <CardDescription>Approved, Rejected, and Error breakdown</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <CheckCircle className="h-6 w-6 mx-auto mb-2" />
            <p>No decision data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate total and percentages
  const total = data.approved + data.rejected + data.error;
  const decisions = [
    { name: 'Approved', value: data.approved, color: 'bg-green-500', textColor: 'text-green-700' },
    { name: 'Rejected', value: data.rejected, color: 'bg-red-500', textColor: 'text-red-700' },
    { name: 'Error', value: data.error, color: 'bg-yellow-500', textColor: 'text-yellow-700' },
  ].filter(item => item.value > 0); // Only show non-zero values

  if (total === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Decision Distribution</CardTitle>
          </div>
          <CardDescription>Approved, Rejected, and Error breakdown</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <CheckCircle className="h-6 w-6 mx-auto mb-2" />
            <p>No decisions recorded</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Decision Distribution</CardTitle>
        </div>
        <CardDescription>
          {total.toLocaleString()} total decisions
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-4 overflow-y-auto">
          {decisions.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{item.value.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1">({percentage.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`${item.color} h-3 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Summary */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="font-medium text-sm mb-2">Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div className="text-center">
              <div className="font-semibold text-green-600">{data.approved}</div>
              <div className="text-muted-foreground">Approved</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-red-600">{data.rejected}</div>
              <div className="text-muted-foreground">Rejected</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-yellow-600">{data.error}</div>
              <div className="text-muted-foreground">Error</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}